/**
 * Application error classes with HTTP status codes.
 */

/**
 * Check if an error matches an app error type by name.
 *
 * tRPC's client re-wraps errors as TRPCClientError in requestAsPromise(),
 * which breaks `instanceof` checks on the client. This helper checks the
 * error's `data.name` (set by our errorFormatter in init.ts) instead,
 * which survives the TRPCClientError wrapping.
 */
type AppErrorName =
  | "AuthenticationError"
  | "AuthorizationError"
  | "NotFoundError"
  | "ConflictError"
  | "CSRFTokenMismatchError"
  | "MediaNotReadyError"
  | "StorageQuotaError"

export function isAppError(error: unknown, name: AppErrorName): boolean {
  if (!error || typeof error !== "object") return false
  // Direct instance (server-side or tests)
  if ((error as any).name === name) return true
  // TRPCClientError wrapping: the original error name is in data.name
  if ((error as any).data?.name === name) return true
  return false
}

export class AuthenticationError extends Error {
  name = "AuthenticationError" as const
  statusCode = 401
  constructor(message = "You must be logged in to access this") {
    super(message)
  }
}

export class AuthorizationError extends Error {
  name = "AuthorizationError" as const
  statusCode = 403
  constructor(message = "You are not authorized to access this") {
    super(message)
  }
}

export class NotFoundError extends Error {
  name = "NotFoundError" as const
  statusCode = 404
  constructor(message = "This could not be found") {
    super(message)
  }
}

export class ConflictError extends Error {
  name = "ConflictError" as const
  statusCode = 409
  constructor(message = "This conflicts with an existing item") {
    super(message)
  }
}

export class CSRFTokenMismatchError extends Error {
  name = "CSRFTokenMismatchError" as const
  statusCode = 401
  constructor(message = "You must provide a valid anti-CSRF token") {
    super(message)
  }
}
